/**
 * LegalAgreementsCard — "Clients & signed copies" panel for one legal
 * document template. Shows every client assigned to this template, the
 * version they were issued/signed, outdated warnings when the template
 * has moved on, and lets the admin upload/download the signed copy.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Users, UserPlus, Download, Upload, RefreshCw, Trash2, Loader2,
  AlertTriangle, FileSignature, Mail, BellRing, Copy,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

const STATUS_STYLES = {
  pending: "bg-amber-50 text-amber-800 border-amber-200",
  signed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  superseded: "bg-zinc-100 text-zinc-500 border-zinc-200",
  terminated: "bg-red-50 text-red-700 border-red-200",
};

function errText(err, fallback) {
  const d = err?.response?.data?.detail;
  return typeof d === "string" ? d : err?.message || fallback;
}

export default function LegalAgreementsCard({ doc, clients }) {
  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignIds, setAssignIds] = useState([]);
  const [assignNotes, setAssignNotes] = useState("");
  const [assignBusy, setAssignBusy] = useState(false);
  // Upload dialog state
  const [uploadFor, setUploadFor] = useState(null);
  const [signerName, setSignerName] = useState("");
  const [signerEmail, setSignerEmail] = useState("");
  const [signedDate, setSignedDate] = useState("");
  const [uploadBusy, setUploadBusy] = useState(false);
  const fileRef = useRef(null);
  // Signature-request dialog state
  const [sigFor, setSigFor] = useState(null); // agreement being sent
  const [sigMode, setSigMode] = useState("send"); // 'send' | 'reminder'
  const [sigToEmail, setSigToEmail] = useState("");
  const [sigToName, setSigToName] = useState("");
  const [sigCc, setSigCc] = useState("");
  const [sigMessage, setSigMessage] = useState("");
  const [sigBusy, setSigBusy] = useState(false);
  const [sigUrl, setSigUrl] = useState("");

  const load = useCallback(async () => {
    if (!doc?.document_id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/legal-documents/${doc.document_id}/agreements`);
      setAgreements(data || []);
    } catch (e) {
      toast.error(errText(e, "Failed to load client agreements"));
    } finally { setLoading(false); }
  }, [doc?.document_id]);
  useEffect(() => { load(); }, [load]);

  const activeClientIds = useMemo(
    () => new Set(agreements
      .filter((a) => ["pending", "signed"].includes(a.status))
      .map((a) => a.client_id)),
    [agreements]);

  const assign = async () => {
    if (!assignIds.length) { toast.error("Pick at least one client"); return; }
    setAssignBusy(true);
    try {
      const { data } = await api.post(`/legal-documents/${doc.document_id}/agreements`, {
        client_ids: assignIds, notes: assignNotes || null,
      });
      toast.success(`Assigned to ${data.created.length} client${data.created.length === 1 ? "" : "s"}`
        + (data.skipped.length ? ` (${data.skipped.length} already assigned)` : ""));
      setAssignOpen(false); setAssignIds([]); setAssignNotes("");
      load();
    } catch (e) { toast.error(errText(e, "Assign failed")); }
    finally { setAssignBusy(false); }
  };

  const reissue = async (a) => {
    if (!window.confirm(`Re-issue "${a.document_title}" to ${a.client_name} at the current version ${a.current_version}? The old ${a.version_string} agreement will be marked superseded.`)) return;
    try {
      await api.post(`/legal-documents/agreements/${a.agreement_id}/reissue`);
      toast.success(`Re-issued at ${a.current_version} — awaiting signature`);
      load();
    } catch (e) { toast.error(errText(e, "Re-issue failed")); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Remove ${a.client_name}'s ${a.version_string} agreement record? Any uploaded signed copy will be deleted too.`)) return;
    try {
      await api.delete(`/legal-documents/agreements/${a.agreement_id}`);
      toast.success("Agreement removed");
      load();
    } catch (e) { toast.error(errText(e, "Delete failed")); }
  };

  const setStatus = async (a, status) => {
    try {
      await api.patch(`/legal-documents/agreements/${a.agreement_id}`, { status });
      load();
    } catch (e) { toast.error(errText(e, "Update failed")); }
  };

  const openUpload = (a) => {
    setUploadFor(a);
    setSignerName(a.signed_by_name || "");
    setSignerEmail(a.signed_by_email || "");
    setSignedDate(a.signed_date || "");
  };

  const uploadSigned = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Choose the signed file first"); return; }
    setUploadBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("signed_by_name", signerName);
      fd.append("signed_by_email", signerEmail);
      fd.append("signed_date", signedDate);
      await api.post(`/legal-documents/agreements/${uploadFor.agreement_id}/signed-file`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Signed copy stored on the client's record");
      setUploadFor(null);
      load();
    } catch (e) { toast.error(errText(e, "Upload failed")); }
    finally { setUploadBusy(false); }
  };

  const downloadSigned = async (a) => {
    try {
      const token = localStorage.getItem("token") || "";
      const res = await fetch(
        `${BACKEND}/api/legal-documents/agreements/${a.agreement_id}/signed-file`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = a.signed_file?.filename || `${a.client_name}_${a.version_string}_signed.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
    } catch { toast.error("Download failed"); }
  };

  const openSigDialog = (a, mode) => {
    setSigFor(a); setSigMode(mode);
    setSigToEmail(a.signature_request?.to_email || a.signed_by_email || "");
    setSigToName(a.signature_request?.to_name || a.signed_by_name || a.client_name || "");
    setSigCc((a.signature_request?.cc_emails || []).join(", "));
    setSigMessage("");
    setSigUrl("");
  };

  const sendSignature = async () => {
    if (!sigToEmail.trim()) { toast.error("Enter the client's email"); return; }
    setSigBusy(true);
    try {
      const path = sigMode === "reminder" ? "send-reminder" : "send-signature-request";
      const { data } = await api.post(
        `/legal-documents/agreements/${sigFor.agreement_id}/${path}`,
        {
          to_email: sigToEmail.trim(),
          to_name: sigToName.trim() || null,
          cc_emails: sigCc.split(",").map((s) => s.trim()).filter(Boolean),
          message: sigMessage.trim() || null,
        });
      toast.success(sigMode === "reminder"
        ? "Reminder email sent" : "Signature request emailed");
      setSigUrl(data.sign_url || "");
      load();
    } catch (e) { toast.error(errText(e, "Send failed")); }
    finally { setSigBusy(false); }
  };

  const copySignLink = async (a) => {
    try {
      const { data } = await api.get(
        `/legal-documents/agreements/${a.agreement_id}/signing-link`);
      await navigator.clipboard.writeText(data.sign_url);
      toast.success("Signing link copied to clipboard");
    } catch (e) { toast.error(errText(e, "Copy failed")); }
  };

  if (!doc) return null;

  return (
    <Card className="rounded-sm" data-testid="legal-agreements-card">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-[#002FA7]" />
            <span className="font-display font-bold text-sm">
              Clients &amp; signed copies ({agreements.length})
            </span>
            {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-zinc-400" />}
          </div>
          <Button size="sm" variant="outline" onClick={() => setAssignOpen(true)}
            data-testid="legal-agreements-assign-btn">
            <UserPlus className="h-4 w-4 mr-1.5" /> Assign to clients
          </Button>
        </div>

        {agreements.length === 0 ? (
          <div className="text-xs text-zinc-500 rounded-sm bg-zinc-50 p-3">
            No clients assigned yet. Use <b>Assign to clients</b> to record who
            this {doc.version_string} template applies to, then upload each
            client&apos;s signed copy for dispute/reference purposes.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-200">
                  <th className="py-1.5 pr-3">Client</th>
                  <th className="py-1.5 pr-3">Version</th>
                  <th className="py-1.5 pr-3">Status</th>
                  <th className="py-1.5 pr-3">Signed</th>
                  <th className="py-1.5 pr-3">Signed copy</th>
                  <th className="py-1.5" />
                </tr>
              </thead>
              <tbody>
                {agreements.map((a) => (
                  <tr key={a.agreement_id} className="border-b border-zinc-100"
                    data-testid={`legal-agreement-row-${a.agreement_id}`}>
                    <td className="py-2 pr-3 font-medium">
                      <Link to={`/clients/${a.client_id}/legal`}
                        className="text-[#002FA7] hover:underline"
                        data-testid={`legal-agreement-client-link-${a.agreement_id}`}>
                        {a.client_name}
                      </Link>
                    </td>
                    <td className="py-2 pr-3">
                      <span className="font-mono">{a.version_string}</span>
                      {a.is_outdated && (
                        <Badge className="ml-1.5 rounded-sm bg-amber-100 text-amber-800 border-transparent text-[9px] align-middle"
                          data-testid={`legal-agreement-outdated-${a.agreement_id}`}>
                          <AlertTriangle className="h-2.5 w-2.5 mr-0.5" />
                          template now {a.current_version}
                        </Badge>
                      )}
                    </td>
                    <td className="py-2 pr-3">
                      <select value={a.status}
                        onChange={(e) => setStatus(a, e.target.value)}
                        className={`rounded-sm border px-1.5 py-0.5 text-[10px] font-semibold ${STATUS_STYLES[a.status] || ""}`}
                        data-testid={`legal-agreement-status-${a.agreement_id}`}>
                        {["pending", "signed", "superseded", "terminated"].map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </td>
                    <td className="py-2 pr-3 text-zinc-600">
                      {a.signed_date || "—"}
                      {a.signed_by_name && <span className="text-zinc-400"> · {a.signed_by_name}</span>}
                    </td>
                    <td className="py-2 pr-3">
                      <div className="flex items-center gap-1.5">
                        {a.signed_file?.path ? (
                          <Button size="sm" variant="outline" className="h-6 px-1.5 text-[10px] rounded-sm"
                            onClick={() => downloadSigned(a)}
                            data-testid={`legal-agreement-download-${a.agreement_id}`}>
                            <Download className="h-3 w-3 mr-1" /> Download
                          </Button>
                        ) : (
                          <span className="text-[10px] text-zinc-400">none</span>
                        )}
                        <Button size="sm" variant="outline" className="h-6 px-1.5 text-[10px] rounded-sm"
                          onClick={() => openUpload(a)}
                          data-testid={`legal-agreement-upload-${a.agreement_id}`}>
                          <Upload className="h-3 w-3 mr-1" />
                          {a.signed_file?.path ? "Replace" : "Upload"}
                        </Button>
                      </div>
                    </td>
                    <td className="py-2 text-right">
                      <div className="flex items-center justify-end gap-1 flex-wrap">
                        {a.status === "pending" && !a.signed_file?.path && (
                          <>
                            <Button size="sm" variant="outline" className="h-6 px-1.5 text-[10px] rounded-sm"
                              onClick={() => openSigDialog(a, "send")}
                              data-testid={`legal-agreement-send-sig-${a.agreement_id}`}>
                              <Mail className="h-3 w-3 mr-1" />
                              {a.signature_request?.last_sent_at ? "Re-send" : "Send for signature"}
                            </Button>
                            {a.signature_request?.last_sent_at && (
                              <Button size="sm" variant="outline" className="h-6 px-1.5 text-[10px] rounded-sm"
                                onClick={() => openSigDialog(a, "reminder")}
                                data-testid={`legal-agreement-send-reminder-${a.agreement_id}`}>
                                <BellRing className="h-3 w-3 mr-1" /> Remind
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" className="h-6 w-6 p-0"
                              title="Copy signing link"
                              onClick={() => copySignLink(a)}
                              data-testid={`legal-agreement-copy-link-${a.agreement_id}`}>
                              <Copy className="h-3 w-3" />
                            </Button>
                          </>
                        )}
                        {a.is_outdated && (
                          <Button size="sm" variant="outline" className="h-6 px-1.5 text-[10px] rounded-sm border-amber-300 text-amber-800"
                            onClick={() => reissue(a)}
                            data-testid={`legal-agreement-reissue-${a.agreement_id}`}>
                            <RefreshCw className="h-3 w-3 mr-1" /> Re-issue
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" className="h-6 px-1.5 text-red-600 hover:text-red-700"
                          onClick={() => remove(a)}
                          data-testid={`legal-agreement-delete-${a.agreement_id}`}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      {/* Assign dialog */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="max-w-lg rounded-sm">
          <DialogHeader>
            <DialogTitle>Assign “{doc.title}” ({doc.version_string}) to clients</DialogTitle>
            <DialogDescription className="text-xs">
              Creates a pending agreement per client at the current template version.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="max-h-[45vh] overflow-y-auto rounded-sm border border-zinc-200 p-2 space-y-1">
              {clients.map((c) => {
                const already = activeClientIds.has(c.client_id);
                const checked = assignIds.includes(c.client_id);
                return (
                  <label key={c.client_id}
                    className={`flex items-center gap-2 px-2 py-1.5 rounded-sm text-sm ${already ? "opacity-45" : "hover:bg-zinc-50 cursor-pointer"}`}>
                    <Checkbox checked={checked} disabled={already}
                      onCheckedChange={(v) => setAssignIds((ids) =>
                        v ? [...ids, c.client_id] : ids.filter((x) => x !== c.client_id))}
                      data-testid={`legal-assign-client-${c.client_id}`} />
                    <span className="flex-1 truncate">{c.company_name}</span>
                    {already && <span className="text-[10px] text-zinc-400">already assigned</span>}
                  </label>
                );
              })}
              {clients.length === 0 && (
                <div className="text-xs text-zinc-500 p-2">No clients found.</div>
              )}
            </div>
            <div>
              <Label className="text-[11px]">Notes (optional — e.g. “Marketing services retainer”)</Label>
              <Input value={assignNotes} onChange={(e) => setAssignNotes(e.target.value)}
                data-testid="legal-assign-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignOpen(false)}>Cancel</Button>
            <Button onClick={assign} disabled={assignBusy || !assignIds.length}
              data-testid="legal-assign-confirm-btn">
              {assignBusy ? "Assigning…" : `Assign (${assignIds.length})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload-signed dialog */}
      <Dialog open={!!uploadFor} onOpenChange={(v) => !v && setUploadFor(null)}>
        <DialogContent className="max-w-md rounded-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSignature className="h-4 w-4 text-[#002FA7]" />
              Signed copy — {uploadFor?.client_name}
            </DialogTitle>
            <DialogDescription className="text-xs">
              Stores the executed copy on this client&apos;s record for future reference.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[11px]">Signed file (PDF or scan)</Label>
              <Input ref={fileRef} type="file" accept=".pdf,image/*,.doc,.docx"
                data-testid="legal-upload-file-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px]">Signed by (name)</Label>
                <Input value={signerName} onChange={(e) => setSignerName(e.target.value)}
                  data-testid="legal-upload-signer-name" />
              </div>
              <div>
                <Label className="text-[11px]">Signed date</Label>
                <Input type="date" value={signedDate} onChange={(e) => setSignedDate(e.target.value)}
                  data-testid="legal-upload-signed-date" />
              </div>
            </div>
            <div>
              <Label className="text-[11px]">Signer email (optional)</Label>
              <Input value={signerEmail} onChange={(e) => setSignerEmail(e.target.value)}
                data-testid="legal-upload-signer-email" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadFor(null)}>Cancel</Button>
            <Button onClick={uploadSigned} disabled={uploadBusy}
              data-testid="legal-upload-confirm-btn">
              {uploadBusy ? "Uploading…" : "Store signed copy"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Signature-request / reminder dialog */}
      <Dialog open={!!sigFor} onOpenChange={(v) => !v && setSigFor(null)}>
        <DialogContent className="max-w-md rounded-sm" data-testid="legal-sig-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {sigMode === "reminder"
                ? <><BellRing className="h-4 w-4 text-[#002FA7]" /> Send reminder</>
                : <><Mail className="h-4 w-4 text-[#002FA7]" /> Send for signature</>}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {sigMode === "reminder"
                ? `Nudge ${sigFor?.client_name} to sign the ${sigFor?.document_title} (${sigFor?.version_string}).`
                : `Emails ${sigFor?.client_name} a signing link + the current PDF. Clients can sign online (typed signature) or upload the signed document.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px]">To (name)</Label>
                <Input value={sigToName} onChange={(e) => setSigToName(e.target.value)}
                  data-testid="legal-sig-to-name" />
              </div>
              <div>
                <Label className="text-[11px]">To (email)</Label>
                <Input type="email" value={sigToEmail}
                  onChange={(e) => setSigToEmail(e.target.value)}
                  data-testid="legal-sig-to-email" />
              </div>
            </div>
            <div>
              <Label className="text-[11px]">CC (comma-separated, optional)</Label>
              <Input value={sigCc} onChange={(e) => setSigCc(e.target.value)}
                placeholder="finance@example.com, admin@example.com"
                data-testid="legal-sig-cc" />
            </div>
            <div>
              <Label className="text-[11px]">Personal note (optional)</Label>
              <textarea rows={3}
                value={sigMessage} onChange={(e) => setSigMessage(e.target.value)}
                className="w-full rounded-sm border border-zinc-300 px-2 py-1.5 text-sm"
                placeholder="e.g. As discussed on Tuesday, please sign the updated SLA…"
                data-testid="legal-sig-message" />
            </div>
            {sigFor?.reminder_count > 0 && sigMode === "reminder" && (
              <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-sm p-2">
                {sigFor.reminder_count} reminder{sigFor.reminder_count === 1 ? "" : "s"} sent so far.
                Auto-reminders stop after 3.
              </div>
            )}
            {sigUrl && (
              <div className="rounded-sm border border-emerald-200 bg-emerald-50 p-2 text-[11px] text-emerald-900 space-y-1">
                <div className="font-semibold">Signing link</div>
                <div className="break-all font-mono">{sigUrl}</div>
                <button type="button" className="underline"
                  onClick={() => { navigator.clipboard.writeText(sigUrl); toast.success("Copied"); }}>
                  Copy to clipboard
                </button>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSigFor(null)}>Close</Button>
            <Button onClick={sendSignature} disabled={sigBusy || !sigToEmail.trim()}
              data-testid="legal-sig-send-btn">
              {sigBusy ? "Sending…"
                : sigMode === "reminder" ? "Send reminder" : "Send email"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
